/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import TextButton from '@design-system/text-button';

interface IProps {
  isOpen: boolean;
  sourceTitle: string | null;
  isPending: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onConfirm: () => void;
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${color.MODAL_COLOR};
  z-index: 100;
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  gap: 16px;
  width: 100%;
  max-width: 380px;
  padding: 24px;
  border-radius: 12px;
  background-color: ${color.WHITE_1};
  box-shadow: ${color.SURFACE_SHADOW};
`;

const CloseButton = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;

const Notice = styled.div`
  padding: 10px 12px;
  border-radius: 6px;
  background-color: ${color.RED_4};
`;

const ButtonRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  gap: 12px;
  margin-top: 8px;
`;

const ExpressionSourceRemoveModal: React.FC<IProps> = ({
  isOpen,
  sourceTitle,
  isPending,
  errorMessage,
  onClose,
  onConfirm,
}) => {
  if (!isOpen) return null;

  return (
    <Backdrop onClick={isPending ? undefined : onClose}>
      <Card onClick={(event) => event.stopPropagation()}>
        <CloseButton>
          <IconBlock
            icon='close'
            design={{
              icon: { size: 'medium', color: color.BLACK_4 },
              box: { event: { cursor: isPending ? 'auto' : 'pointer' } },
            }}
            click={isPending ? undefined : { handler: onClose }}
          />
        </CloseButton>
        <TextBlock
          text='정말로 삭제하시겠습니까?'
          design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }}
        />
        <Notice>
          <TextBlock
            text={sourceTitle ? `“${sourceTitle}” 소스를 휴지통으로 이동합니다.` : '선택한 소스를 휴지통으로 이동합니다.'}
            design={{ text: { size: 'small', color: color.RED_1 } }}
          />
        </Notice>
        {errorMessage && (
          <TextBlock
            text={errorMessage}
            design={{ text: { size: 'small', color: color.RED_2 } }}
          />
        )}
        <ButtonRow>
          <TextButton
            text='취소'
            theme={ETheme.GRAY}
            kind='outlined'
            width='100px'
            disabled={isPending}
            handleClick={onClose}
          />
          <TextButton
            text='삭제'
            theme={ETheme.RED}
            width='100px'
            disabled={isPending}
            isLoading={isPending}
            handleClick={onConfirm}
          />
        </ButtonRow>
      </Card>
    </Backdrop>
  );
};

export default ExpressionSourceRemoveModal;
